/**
 * How dates are written, on the browser's side of the line.
 *
 * The server renders the account's choice onto <html> (see
 * ApplicationHelper#html_settings) and lib/themeSync.ts carries it across Turbo
 * visits, so every date the browser formats itself can ask one function rather
 * than each screen holding its own Intl formatter and its own opinion.
 *
 * Kept deliberately small: four styles, and the same four the server knows
 * (app/models/date_style.rb). A date shown on screen and the same date shown in
 * a JSON prop have to look identical, and they only do while both sides
 * implement the same list.
 */

export type DateStyleKey = 'long' | 'dmy' | 'mdy' | 'ymd'

const LONG = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
const LONG_SHORT = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' })
const WEEKDAY_LONG = new Intl.DateTimeFormat('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })

export function dateStyle(): DateStyleKey {
  const chosen = document.documentElement.dataset.dateStyle
  return chosen === 'dmy' || chosen === 'mdy' || chosen === 'ymd' ? chosen : 'long'
}

/** Rails date columns are "YYYY-MM-DD"; parse as local midnight, never UTC. */
export function asDate(value: string | Date): Date {
  if (value instanceof Date) return value
  const [year, month, day] = value.slice(0, 10).split('-').map(Number)
  return new Date(year, month - 1, day)
}

function pad(value: number) {
  return String(value).padStart(2, '0')
}

/** A whole date — "24 Mar 2026", or the numeric shape that was chosen. */
export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '—'

  const date = asDate(value)
  const day = pad(date.getDate())
  const month = pad(date.getMonth() + 1)

  switch (dateStyle()) {
    case 'dmy':
      return `${day}/${month}/${date.getFullYear()}`
    case 'mdy':
      return `${month}/${day}/${date.getFullYear()}`
    case 'ymd':
      return `${date.getFullYear()}-${month}-${day}`
    default:
      return LONG.format(date)
  }
}

/**
 * The compact form a dense list uses — "16 Jul" rather than "16 Jul 2026".
 * Only the written-out style has one: half a numeric date is not a date, so
 * the other three give the whole thing.
 */
export function formatDateShort(value: string | Date | null | undefined): string {
  if (!value) return '—'
  return dateStyle() === 'long' ? LONG_SHORT.format(asDate(value)) : formatDate(value)
}

/** "Tuesday 24 March" — a heading, so the weekday leads whatever the style. */
export function formatDayHeading(value: string | Date): string {
  const date = asDate(value)
  if (dateStyle() === 'long') return WEEKDAY_LONG.format(date)

  return `${date.toLocaleDateString('en-GB', { weekday: 'long' })} ${formatDate(date)}`
}

/** Monday unless the account said otherwise. */
export function weekStartsOnSunday(): boolean {
  return document.documentElement.dataset.weekStart === 'sunday'
}

/**
 * The seven weekday labels, rotated to start where the account's week starts.
 * One array so a calendar's headings and its grid can never disagree about
 * which column Monday is in.
 */
export function weekdayLabels(): string[] {
  const monday = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
  return weekStartsOnSunday() ? ['Sun', ...monday.slice(0, 6)] : monday
}

/** How many days into the week a Date falls, counting from the account's own start. */
export function weekdayIndex(date: Date): number {
  return weekStartsOnSunday() ? date.getDay() : (date.getDay() + 6) % 7
}
